/**
 * Purchasing today (TASK-0250, FR-TODAY): the rows the Today screen and the
 * order guide board's strip show, and where each one goes. Pure, so both
 * screens and their tests say the same thing.
 *
 * A row is left out at zero: a grey zero reads as an alarm someone already
 * dismissed. Each link opens the list its count was taken from:
 *  - deliveries to check: the order guide board (the flagged vendors);
 *  - invoices waiting: the inbox's Ready filter, last 14 days, held-back parts left out;
 *  - photos that could not be read: the inbox's Failed filter, last 14 days;
 *  - invoices with held-back lines: the inbox's Ready filter, held-back parts
 *    only, last 14 days (the count is of invoices, not of their lines);
 *  - shelf labels: Organize › Labels, waiting to print.
 * "Deliveries to check" is a pattern in the receipts, not a fact, and says so.
 */

import { countLabel, num } from '@/lib/format';
import type { DeliveryToCheck, PurchasingToday } from './types';

export type TodayLink =
  | { to: '/purchasing/guide' }
  | {
      to: '/purchasing/import';
      search: {
        state: 'matched' | 'failed';
        kind?: 'original' | 'followup';
        since: string;
      };
    }
  | { to: '/organize'; search: { tab: 'labels'; labelStatus: 'pending' } };

export type TodayItemId =
  | 'deliveries'
  | 'invoices'
  | 'unreadable'
  | 'held-back'
  | 'labels';

export interface TodayItem {
  id: TodayItemId;
  label: string;
  detail: string;
  count: number;
  tone: 'warn' | 'signal' | 'info';
  link: TodayLink;
}

/** `KENOVER: nothing recorded in 12 days, usually every 7`, or the first names. */
export function deliveriesDetail(vendors: readonly DeliveryToCheck[]): string {
  if (vendors.length === 0) return '';
  if (vendors.length === 1) {
    const v = vendors[0]!;
    return `${v.name}: nothing recorded in ${countLabel(v.daysSilent, 'day')}, usually every ${num(v.usualGapDays)}`;
  }
  const shown = vendors.slice(0, 2).map((v) => `${v.name} (${num(v.daysSilent)} days)`);
  const rest = vendors.length - shown.length;
  return `Later than their usual pattern: ${shown.join(', ')}${rest > 0 ? ` and ${num(rest)} more` : ''}`;
}

/** `Read, not recorded yet · 3 aimed at an order · 2 to match to an order`. */
export function invoicesDetail(waiting: PurchasingToday['invoicesWaiting']): string {
  const parts = ['Read, not recorded yet'];
  if (waiting.aimed > 0) parts.push(`${num(waiting.aimed)} aimed at an order`);
  if (waiting.choosePo > 0) parts.push(`${num(waiting.choosePo)} to match to an order`);
  return parts.join(' · ');
}

export function purchasingTodayItems(
  data: PurchasingToday | null | undefined
): TodayItem[] {
  if (!data) return [];
  const items: TodayItem[] = [];
  const window = `last ${num(data.windowDays)} days`;
  if (data.deliveriesToCheck.count > 0) {
    items.push({
      id: 'deliveries',
      label: 'Deliveries that look missing',
      detail: deliveriesDetail(data.deliveriesToCheck.vendors),
      count: data.deliveriesToCheck.count,
      tone: 'warn',
      link: { to: '/purchasing/guide' },
    });
  }
  if (data.invoicesWaiting.count > 0) {
    items.push({
      id: 'invoices',
      label: 'Invoices waiting',
      detail: `${invoicesDetail(data.invoicesWaiting)} (${window})`,
      count: data.invoicesWaiting.count,
      tone: 'signal',
      link: {
        to: '/purchasing/import',
        search: { state: 'matched', kind: 'original', since: data.since },
      },
    });
  }
  if (data.unreadablePhotos.count > 0) {
    items.push({
      id: 'unreadable',
      label: 'Invoice photos that could not be read',
      detail: `Retry the read or upload a clearer photo (${window})`,
      count: data.unreadablePhotos.count,
      tone: 'warn',
      link: { to: '/purchasing/import', search: { state: 'failed', since: data.since } },
    });
  }
  if (data.heldBackWaiting.count > 0) {
    items.push({
      id: 'held-back',
      // The count is of held-back invoices (imports), not of their lines.
      label: 'Invoices with held-back lines',
      detail: `Lines left off an invoice, still to record (${window})`,
      count: data.heldBackWaiting.count,
      tone: 'info',
      link: {
        to: '/purchasing/import',
        search: { state: 'matched', kind: 'followup', since: data.since },
      },
    });
  }
  if (data.labelsWaiting.count > 0) {
    items.push({
      id: 'labels',
      label: 'Shelf labels to print',
      detail: 'Waiting at the label station',
      count: data.labelsWaiting.count,
      tone: 'info',
      link: { to: '/organize', search: { tab: 'labels', labelStatus: 'pending' } },
    });
  }
  return items;
}
